const get = require('lodash/get')
const isArray = require('lodash/isArray')

const createShipmentSnapshot = require('./shipment-create-snapshot')
const updateShipmentSnapshot = require('./shipment-update-snapshot')
const createFollowUpShipment = require('./shipment-create-follow-up')
const createBulkShipmentSnapshots = require('./shipment-bulk-create-snapshots')
const findShipment = require('./shipment-find')
const saveShipmentChanges = require('./shipment-save-changes')
const saveAdjustments = require('./shipment-save-adjustments')
const addShipmentComment = require('./shipment-add-comment')
const addAdjustmentReasons = require('./shipment-add-adjustment-reasons')
const addProductToShipment = require('./shipment-add-product')
const removeProductFromShipment = require('./shipment-remove-product')
const markShipmentVoid = require('./shipment-mark-void')
const findAllSurveysForSnapshot = require('./survey-find-all-for-snapshot')
const findAllSurveysForShipment = require('./survey-find-all-for-shipment')
const updateCRQuantities = require('./update-cr-quantities')
const updateCRQuantitiesStats = require('./update-cr-quantities-stats')
const { exportShipmentsData, exportShipments } = require('./sl-shipment-export')
const { exportLMDShipments } = require('./psm-shipment-export')
const { exportShipmentsListData, exportShipmentsList } = require('./sl-shipment-list-export')
const findSurvey = require('./survey-find')
const findAdjustments = require('./shipment-find-adjustments')
const createSurvey = require('./survey-create')
const { bulkSnapshotsFromSheet } = require('./shipment-bulk-snapshot-from-allocation-sheet')
const validateSheetLocations = require('./validate-sheet-locations.js')
const { getImportTemplate } = require('./shipment-get-import-template')
const { getOutboundExcelTemplate } = require('./shipment-get-outbound-template')
const { convertExcelOutboundShipmentToCsv } = require('./psm-outbound-shipment-excel-to-csv-converter')
const { importShipments, importShipmentsFromSheet } = require('./shipment-import')
const { reschedule } = require('./shipment-reschedule')
const setupShipmentApi = require('./setup')
const { EntityApi, ProxifiedEntityApi } = require('../common')
const wrap = require('../utils/wrap-api')
const ShipmentPouchAdapter = require('./data-access/shipment-pouch-adapter')
const DataShipmentAdapter = require('./data-access/data-shipment-adapter')
const ShipmentSnapshotAdapter = require('./data-access/shipment-snapshot-adapter')
const getReturnsNote = require('./shipment-get-returns-note')
const { bulkTranslateShipmentProducts } = require('./tools/bulk-translate-shipment-products')
const { exportWareHouseFunderShipments } = require('./export-warehouse-funder-shipments')
const parseShipmentUploadData = require('./tools/parse-shipment-upload-data')
const psmShipmentUpload = require('./psm-shipment-upload')
const { fromRelationalModel } = require('./tools/from-relational-model')
const docsToShipmentRecords = require('./tools/docs-to-shipment-records')
const { calculateShipmentVolumeAndPrice } = require('./tools/calculate-shipment-volume-price')
const { calculateDistance, gpsValidation, addGpsValidationFailComment } = require('./shipment-gps-validation')
const bulkAdvanceSnapshots = require('./shipment-bulk-advance-snapshots')
const getExternalShipment = require('./get-external-shipment')
const getShipmentByNumber = require('./get-shipment-by-number')
const getByParentDocId = require('./shipment-get-by-parent-doc-id')
const getRawShipmentsById = require('./get-raw-shipments-by-id')
const { SHIPMENT_STATUS } = require('./constants')
const { OtpApi } = require('./otp.js')
const ShipmentPriceListApi = require('./shipment-price-list')
const { applyLMDUserFilters } = require('./tools/apply-lmd-user-filters')
const { mergeFilterObjects } = require('../location/tools/filters')
const keyBy = require('lodash/keyBy')

// Normalizes a batch ID so the product and manufacturer parts are always lowercase,
// while preserving the original casing of the batchNo segment.
// LMD snapshots can store batch IDs with mixed-case product parts
// (e.g. product:GHSC-AID0276:manufacturer:chemonics:batchNo:ZEA25016A),
// but master-data always stores them lowercase.
function normalizeBatchId (batchId) {
  const batchNoIdx = batchId.indexOf(':batchNo:')
  if (batchNoIdx === -1) return batchId
  return batchId.slice(0, batchNoIdx).toLowerCase() + batchId.slice(batchNoIdx)
}

const rawShipmentMethods = {
  addComment: addShipmentComment,
  addAdjustmentReasons,
  createSnapshot: createShipmentSnapshot,
  updateSnapshot: updateShipmentSnapshot,
  createFollowUpShipment,
  createBulkSnapshots: createBulkShipmentSnapshots,
  find: findShipment,
  findById: findShipment.findById,
  findByIds: findShipment.findByIds,
  saveChanges: saveShipmentChanges,
  reschedule: reschedule,
  addProduct: addProductToShipment,
  removeProduct: removeProductFromShipment,
  markVoid: markShipmentVoid,
  bulkSnapshotsFromSheet,
  validateSheetLocations,
  getImportTemplate,
  getOutboundExcelTemplate,
  convertExcelOutboundShipmentToCsv,
  calculateDistance,
  gpsValidation,
  addGpsValidationFailComment,
  importShipmentsFromSheet,
  importShipments,
  slExport: exportShipments,
  slExportData: exportShipmentsData,
  exportLMDShipments: exportLMDShipments,
  slShipmentListExport: exportShipmentsList,
  slShipmentListExportData: exportShipmentsListData,
  getReturnsNote,
  updateCRQuantities,
  updateCRQuantitiesStats,
  bulkTranslateShipmentProducts,
  exportWareHouseFunderShipments,
  bulkAdvanceSnapshots,
  getExternalShipment,
  getShipmentByNumber,
  getRawShipmentsById,
  getByParentDocId
}

const rawAdjustmentMethods = {
  draft: saveShipmentChanges.draftAdjustments,
  submit: saveAdjustments,
  find: findAdjustments
}

const rawSurveyMethods = {
  create: createSurvey,
  findAllForSnapshot: findAllSurveysForSnapshot,
  find: findSurvey,
  findAllForShipment: findAllSurveysForShipment
}

const entityName = 'shipment'

const methodsNeedingProxy = [
  {
    methodName: 'listLastMileDeliveries',
    restAction: 'GET',
    cache: false
  },
  {
    methodName: 'getLastMileDeliveriesFilterValues',
    restAction: 'GET',
    cache: false
  },
  'updateRoute',
  // we choose create (aka. POST) for calculate volume because
  // with shipment ids we can easily exceed the URL parameter limit,
  {
    methodName: 'calculateVolume',
    restAction: 'create'
  },
  // counts-based volume calculation used as CouchDB fallback when postgres line tables are empty
  {
    methodName: 'calculateVolumeFromCounts',
    restAction: 'create'
  }
]

class ShipmentApi extends ProxifiedEntityApi {
  constructor (state, agaveAdapter, pgConnection, logger, mainApi) {
    const { user, shipmentsDb } = state
    const adapter = new ShipmentPouchAdapter(user, shipmentsDb)
    super(entityName, methodsNeedingProxy, !pgConnection, adapter, agaveAdapter)

    if (pgConnection) {
      this.dataShipmentAdapter = new DataShipmentAdapter(pgConnection, user, logger, { withGlobalAccessUser: state.withGlobalAccessUser })
      this.shipmentSnapshotAdapter = new ShipmentSnapshotAdapter(pgConnection, user.name, logger, { withGlobalAccessUser: state.withGlobalAccessUser })
      this.HAS_PG_ACCESS = true
    }
    this.state = state
    // Store user for use in methods
    this.user = user
    // TODO: remove this when all raw methods have been ported
    wrap(rawShipmentMethods, state, setupShipmentApi, null, this)
    this.adjustment = {}
    wrap(rawAdjustmentMethods, state, setupShipmentApi, null, this.adjustment)

    this.otp = new OtpApi({ shipmentsDb, agaveAdapter, pgConnection })
    this.mainApi = mainApi
    this.priceList = new ShipmentPriceListApi(mainApi, pgConnection)
  }

  async getBucketObject (params) {
    if (!this.agaveAdapter) {
      throw new Error('getBucketObject requires agaveAdapter')
    }
    return this.agaveAdapter.get('shipment/bucket-object', params)
  }

  async logToS3 (params) {
    if (!this.agaveAdapter) {
      throw new Error('logToS3 requires agaveAdapter')
    }
    return this.agaveAdapter.post('shipment/log-to-s3', params)
  }

  resolveShipmentPrices (shipments) {
    return this.priceList.resolveShipmentPrices(shipments)
  }

  decorateShipmentSnapshotsWithResolvedPrices (shipments, options) {
    return this.priceList.decorateShipmentSnapshotsWithResolvedPrices(shipments, options)
  }

  async updateRoute ({ shipmentIds, routeId }) {
    if (routeId === undefined) {
      throw new Error(`Cannot move shipment to route: routeId is required parameter`)
    }
    if (!shipmentIds || !shipmentIds.length) {
      throw new Error(`Cannot move shipment to route: shipmentIds is required parameter or it can't be empty`)
    }

    // Update Postgres and CouchDB in parallel
    const postgresUpdate = this.dataShipmentAdapter.updateWhere(
      { filter: { shipment_id: shipmentIds } },
      { route_id: routeId })

    // Update CouchDB snapshots in parallel for offline driver access
    const couchdbUpdate = this.updateRouteInCouchDB(shipmentIds, routeId).catch(err => {
      // Log error but don't fail the whole operation if CouchDB update fails
      // Postgres update is the source of truth, CouchDB sync will catch up via sync pipeline
      console.error('Error updating route in CouchDB:', err)
      return null
    })

    await Promise.all([postgresUpdate, couchdbUpdate])
    return postgresUpdate
  }

  /**
   * Update routeId in CouchDB snapshots for shipments.
   * This ensures drivers using offline access can see shipments with updated routes immediately.
   * @param {Array<string>} shipmentIds - Array of shipment IDs to update
   * @param {string} routeId - The route ID to assign
   * @returns {Promise<void>}
   */
  async updateRouteInCouchDB (shipmentIds, routeId) {
    if (!this.mainApi || !this.mainApi.state) {
      return // Skip if state is not available (e.g., in test environments)
    }

    const updateSnapshot = require('./shipment-update-snapshot')
    const { state } = this.mainApi

    // Get all snapshots for all shipments in parallel
    const snapshotPromises = shipmentIds.map(shipmentId =>
      state.dal.shipment.getAllDocsWithPrefix(state.shipmentsDb, shipmentId)
    )

    const snapshotResults = await Promise.all(snapshotPromises)

    // Update each snapshot with the new routeId, and collect affected order IDs
    const updateTasks = []
    const orderIds = new Set()
    for (const result of snapshotResults) {
      for (const row of result.rows || []) {
        const doc = row.doc
        if (doc && doc.type === 'snapshot') {
          if (doc.orderId) orderIds.add(doc.orderId)
          // Only update if routeId is actually changing
          if (doc.routeId !== routeId) {
            updateTasks.push({
              id: doc._id,
              promise: updateSnapshot(state, {
                snapshotId: doc._id,
                counts: doc.counts,
                shipmentTypeId: doc.shipmentTypeId,
                planningType: doc.planningType,
                createdAt: doc.createdAt,
                createdBy: doc.createdBy,
                _rev: doc._rev,
                funderId: doc.funderId,
                programId: doc.programId,
                routeId, // Update routeId
                orderId: doc.orderId,
                externalShipmentId: doc.externalShipmentId
              })
            })
          }
        }
      }
    }

    const results = await Promise.allSettled(updateTasks.map(t => t.promise))
    const failures = []
    results.forEach((res, idx) => {
      if (res.status === 'rejected') {
        const failedId = updateTasks[idx] && updateTasks[idx].id
        // Log each failure with snapshot id for easier troubleshooting
        console.error(`Error updating snapshot ${failedId} in CouchDB:`, res.reason)
        failures.push(failedId || 'unknown')
      }
    })

    if (failures.length) {
      // Throw a summarized error; caller of updateRouteInCouchDB already handles/logs
      throw new Error(`Failed to update ${failures.length} snapshot(s) in CouchDB`)
    }

    // Update the routeId on the order documents so that future packed snapshots
    // created from the order doc (via moveShipmentStatus) inherit the correct routeId.
    if (state.orderDb && orderIds.size > 0) {
      await updateOrderDocsRouteInCouchDB(state.orderDb, [...orderIds], routeId)
    }
  }

  async unassignRoute ({ shipmentIds }) {
    if (!shipmentIds || !shipmentIds.length) {
      throw new Error(`Cannot unassign route: shipmentIds is required parameter or it can't be empty`)
    }

    // Update PostgreSQL
    const postgresUpdate = this.dataShipmentAdapter.updateWhere(
      { filter: { shipment_id: shipmentIds } },
      { route_id: null })

    // Update CouchDB snapshots (mirrors updateRoute pattern)
    const couchdbUpdate = this.updateRouteInCouchDB(shipmentIds, null).catch(err => {
      console.error('Error unassigning route in CouchDB:', err)
      return null
    })

    await Promise.all([postgresUpdate, couchdbUpdate])
  }

  async findByRoutes ({ routeIds }) {
    const shipmentEntities = await this.adapter.find({selector: {routeId: {$in: routeIds}}})
    const shipmentSnapshots = shipmentEntities.map(doc => ({...doc, _id: doc.id}))
    const shipments = docsToShipmentRecords(undefined, shipmentSnapshots, {
      useLocationAsOrigin: true
    })
    return shipments
  }

  getExternalShipment ({ shipmentNumber }) {
    if (!shipmentNumber) {
      throw new Error('Missing shipmentNumber parameter')
    }
    return getExternalShipment(this, { shipmentNumber })
  }

  getShipmentByNumber ({ shipmentNumber }) {
    if (!shipmentNumber) {
      throw new Error('Missing shipmentNumber parameter')
    }
    return getShipmentByNumber(this, { shipmentNumber })
  }

  getRawShipmentsById ({ shipmentId }) {
    if (!shipmentId) {
      throw new Error('Missing shipmentId parameter')
    }
    return getRawShipmentsById(this, { shipmentId })
  }

  parseShipmentUploadData (shipmentRows, programId, funderId, options) {
    return parseShipmentUploadData(shipmentRows, this.mainApi.location, this.mainApi.configuration, programId, funderId, options)
  }

  async uploadShipments (formData, hasToAddRoutesToShipments, shouldSync = false) {
    formData.append('hasToAddRoutesToShipments', hasToAddRoutesToShipments ? 'true' : 'false')
    const uploadedShipments = await this.agaveAdapter.create('shipment/upload', formData)
    if (shouldSync) {
      await this.state.shipmentsDb.sync(this.state.shipmentsDb, this.state.shipmentsRemoteDb)
    }
    return uploadedShipments
  }

  shipmentUpload (funderShipments, routes, crypto) {
    return psmShipmentUpload(this.mainApi.state, {
      funderShipments,
      routes,
      crypto,
      routesApi: this.mainApi.routes,
      shipmentApi: this.mainApi.shipment
    })
  }

  /**
   * Proxified method (see ProxifiedEntityApi) to list last mile deliveries.
   * @param {Object} params - Contains Filters to apply, pageNumber and seperateByCompletion which separates complete from incomplete.
   * @returns {Promise<Array>} List of last mile deliveries.
   */
  async listLastMileDeliveries ({
    filters = {},
    pageNumber = 1,
    separateByCompletion = false,
    addSnapshotsData = true, // Enrich shipments with snapshotDates, GPS validation, OTP, and driverName
    addGroupOrderId = false, // Get related orders' groupOrder id
    onlyCount = false, // Only return the count of the shipments matching the filter
    applyUserFilters = false, // to be only called from agave controller to merge with default user filters
    forDownload = false, // for lmd shipment download only
    deliveryType = DataShipmentAdapter.SHIPMENT_TYPE_LAST_MILE_DELIVERY // we account for interwarehouse shipments in addition to of LMD and also allow to get just all shipments, unfiltered. TODO: This is misleading, should rename this method to something less LMD-specific
  }) {
    // clone filters but "state" property
    const processedFilters = this.applyUserFiltersIfNeeded({
      filters,
      applyUserFilters
    })

    // Manage the STATE property that is in fact part of the destination_id string. STATE meaning administrative boundary for a country
    const stateSQLExpression = 'substring(destination_id from \'state:([^:]+)\')'

    // create state filter based on destination_id field
    const stateFilter = filters.state
    if (stateFilter) {
      delete processedFilters.state
      processedFilters[stateSQLExpression] = stateFilter
    }

    // getList manages offsets separated from the rest of the filter
    const offset = filters.offset
    if (offset) {
      delete processedFilters.offset
    }

    // getList manages limits separated from the rest of the filter
    const limit = filters.limit
    if (limit) {
      delete processedFilters.limit
    }

    const adapterFilter = DataShipmentAdapter.shipmentTypeFilters[deliveryType]

    // When forDownload=true, we need to pass filters with state included, but getLastMileDeliveriesShipmentsForDownload
    // expects state as a plain string (not the SQL expression), so we merge filters with processedFilters to get
    // all processed filters, but keep state from the original filters if it exists.
    // When forDownload=false, merge adapterFilter with processedFilters.
    let mergedFilters
    if (forDownload) {
      // For download, we need state as a plain string (not SQL expression)
      // Extract state from SQL expression if it exists, or use filters.state
      const stateValue = stateFilter || processedFilters[stateSQLExpression] || filters.state

      // Merge processedFilters with filters, ensuring state is preserved as plain string
      // CRITICAL: Preserve route_id: [] from processedFilters if it exists (for drivers with no routes)
      // SECURITY: Always enforce empty array for drivers with no routes, even if user tries to pass route_id
      // This prevents drivers from bypassing the restriction by passing route_id in filters
      const preserveRouteId = Array.isArray(processedFilters.route_id) && processedFilters.route_id.length === 0
      mergedFilters = { ...processedFilters, ...filters }

      // Always enforce route_id: [] for drivers with no routes (security: prevent bypass attempts)
      if (preserveRouteId) {
        mergedFilters.route_id = []
      }

      // Remove the SQL expression version of state if it exists
      if (mergedFilters[stateSQLExpression]) {
        delete mergedFilters[stateSQLExpression]
      }

      // Ensure state is set as plain string for getLastMileDeliveriesShipmentsForDownload
      if (stateValue) {
        mergedFilters.state = stateValue
      }
    } else {
      mergedFilters = mergeFilterObjects(processedFilters, adapterFilter)
    }

    const result = forDownload ? await this.dataShipmentAdapter.getLastMileDeliveriesShipmentsForDownload(mergedFilters) : await this.dataShipmentAdapter.getList({
      // We want to ensure Postgres will return the most recent shipments first and also will always stick to same ordering
      // when there are multiple shipments with the same created_at
      ordering: ['-created_at', 'shipment_id'],
      filter: mergedFilters,
      limit,
      offset,
      addGroupOrderId,
      ...(onlyCount
        ? {
          skipCount: false,
          skipData: true
        }
        : {}
      )
    })

    if (onlyCount) {
      return result.count
    }

    const rows = forDownload ? result : result.results
    const rdsShipments = rows.map(row => {
      const shipment = fromRelationalModel({ shipment: row })
      if (addGroupOrderId) { // add group id to the built entity
        shipment.orderGroupId = get(row, 'order[0].group_id')
      }
      return shipment
    })

    if (addSnapshotsData && !forDownload) {
      const shipmentIds = rows.map(row => row.shipment_id)
      const snapshotData = this.shipmentSnapshotAdapter
        ? await this.shipmentSnapshotAdapter.getLmdSnapshotData(shipmentIds)
        : await this.getSnapshotDataForShipments(rows)

      rdsShipments.forEach(rdsShipment => {
        const match = snapshotData.find(item => item.id === rdsShipment.id)
        if (match) {
          rdsShipment.snapshotDates = match.snapshotDates
          rdsShipment.gpsValidationFailComment = match.gpsValidationFailComment
          rdsShipment.isGpsAuthenticated = match.isGpsAuthenticated
          rdsShipment.otp = match.otp
          rdsShipment.driverName = match.driverName
          rdsShipment.history = match.history
          rdsShipment.orderId = match.orderId
        }
      })
    }

    if (forDownload) {
      return { shipments: rdsShipments }
    }

    if (!separateByCompletion) {
      return { shipments: rdsShipments, total: result.count }
    } else {
      return rdsShipments.reduce((acc, shipment) => {
        if (shipment.status === SHIPMENT_STATUS.RECEIVED) {
          acc.complete.push(shipment)
        } else {
          acc.incomplete.push(shipment)
        }
        return acc
      }, {incomplete: [], complete: []})
    }
  }

  /**
   * @deprecated Fallback for offline/PouchDB mode. Use shipmentSnapshotAdapter.getLmdSnapshotData() instead.
   * Retrieve and normalize snapshot data for a list of pg retrieved shipments.
   *
   * @async
   * @param {Array<{ shipment_id: string }>} shipments
   *   Array of shipment descriptor objects. Each object must contain a shipment_id property
   *   used as the prefix when fetching snapshot documents.
   *
   * @returns {Promise<Array<Object>>}
   *   Promise resolving to an array of normalized shipment record objects produced by docsToShipmentRecords.
   *
   * @throws {Error}
   *   Propagates any errors thrown by the underlying DAL calls or docsToShipmentRecords transformation.
   */
  async getSnapshotDataForShipments (shipments) {
    // let's get additional data from CouchDB
    const shipmentSnapshotsPromises = shipments.map(({shipment_id: shipmentId}) => this.mainApi.state.dal.shipment.getAllDocsWithPrefix(this.mainApi.state.shipmentsDb, shipmentId))
    const shipmentSnapshotsData = await Promise.all(shipmentSnapshotsPromises)

    // get a plain simple array of snapshots to feed docsToShipmentRecords
    const shipmentSnapshots = []
    shipmentSnapshotsData.forEach(item => {
      item.rows.forEach(row => {
        shipmentSnapshots.push(row.doc)
      })
    })
    return docsToShipmentRecords(undefined, shipmentSnapshots, {useLocationAsOrigin: true})
  }

  // TODO - TECH DEBT
  // At some point this function should be made PG only.
  // The right time would be when more data is available directly from PG, like:
  // - snapshots with batch level details
  // - master data that includes batch details
  // This would avoid multiple calls to CouchDB and PG and we could get this all done
  // in one single PG query.
  async getShipments (routeId) {
    const route = await this.mainApi.routes.pgRouteViewDataAdapter.getOne(routeId, {
      whereCondition: 'couchdb_id'
    })
    if (!route) {
      throw new Error(`Route with id ${routeId} not found`)
    }

    const couchShipmentSnapshots = await this.getSnapshotDataForShipments(route.shipments)

    const { shipmentContent, batchIds, locationIds } = route.shipments.reduce(
      (acc, pgShipment) => {
        if (pgShipment.origin_id) acc.locationIds.add(pgShipment.origin_id)
        if (pgShipment.destination_id) acc.locationIds.add(pgShipment.destination_id)

        const couchShipment = couchShipmentSnapshots.find(
          snapshot => snapshot.id === pgShipment.shipment_id
        )

        if (!couchShipment) {
          console.log('No snapshot found for shipment:', pgShipment.shipment_id)
          return acc
        }

        const normalizedCounts = {}
        Object.keys(couchShipment.counts || []).forEach(batchId => {
          const normalizedId = normalizeBatchId(batchId)
          acc.batchIds.add(normalizedId)
          normalizedCounts[normalizedId] = couchShipment.counts[batchId]
        })

        return {
          ...acc,
          shipmentContent: {
            ...acc.shipmentContent,
            [pgShipment.id]: normalizedCounts
          }
        }
      },
      { shipmentContent: {}, batchIds: new Set(), locationIds: new Set() }
    )

    const locations = await this.mainApi.location.getByIds(Array.from(locationIds))
    const funders = await this.mainApi.funders.list({ unfiltered: false })

    const batchesResult = await this.state.masterDataDB.find({
      selector: {
        $and: [
          { type: { $eq: 'batch' } },
          { _id: { $in: Array.from(batchIds) } }
        ]
      },
      limit: Number.MAX_SAFE_INTEGER,
      fields: ['_id', 'manufacturer', 'expiry', 'alias']
    })

    const batchesByNormalizedId = keyBy(batchesResult.docs, '_id')

    const unresolvedBatchIds = Array.from(batchIds).filter(
      id => !batchesByNormalizedId[id] || !get(batchesByNormalizedId[id], 'alias.one')
    )

    if (unresolvedBatchIds.length > 0) {
      const extractProductId = batchId => {
        const match = batchId.match(/^(product:[^:]+):manufacturer:/)
        return match ? match[1] : null
      }
      const extractManufacturerSuffix = batchId => {
        const match = batchId.match(/^product:[^:]+(:manufacturer:.+)$/)
        return match ? match[1] : null
      }

      const unresolvedProductIds = unresolvedBatchIds
        .map(id => extractProductId(id))
        .filter(id => id !== null)

      const unresolvedProducts = await this.mainApi.product.getByIds(unresolvedProductIds)
      const unresolvedProductsById = keyBy(unresolvedProducts, '_id')

      const fallbackIdByNormalized = {}
      for (const normalizedId of unresolvedBatchIds) {
        const productId = extractProductId(normalizedId)
        const suffix = extractManufacturerSuffix(normalizedId)
        if (!productId || !suffix) continue
        const genericParent = unresolvedProductsById[productId] && unresolvedProductsById[productId].genericParent
        if (!genericParent) continue
        fallbackIdByNormalized[normalizedId] = `product:${genericParent.toLowerCase()}${suffix}`
      }

      const fallbackBatchIds = Object.values(fallbackIdByNormalized)
      if (fallbackBatchIds.length > 0) {
        const fallbackResult = await this.state.masterDataDB.find({
          selector: {
            $and: [
              { type: { $eq: 'batch' } },
              { _id: { $in: fallbackBatchIds } }
            ]
          },
          limit: Number.MAX_SAFE_INTEGER,
          fields: ['_id', 'manufacturer', 'expiry', 'alias']
        })

        const fallbackBatchesById = keyBy(fallbackResult.docs, '_id')
        for (const [normalizedId, fallbackId] of Object.entries(fallbackIdByNormalized)) {
          const batch = fallbackBatchesById[fallbackId]
          if (batch) {
            batchesByNormalizedId[normalizedId] = batch
          }
        }
      }
    }

    const productIds = batchesResult.docs.map(batch => `product:${batch.manufacturer}`)
    const products = await this.mainApi.product.getByIds(productIds)

    return {
      route,
      shipmentContent,
      batches: batchesByNormalizedId,
      locations: keyBy(locations, '_id'),
      products: keyBy(products, '_id'),
      funders: keyBy(funders, '_id')
    }
  }

  /**
   * Proxified method (see ProxifiedEntityApi) to get last mile deliveries filter values for a given date.
   * @param {Date} startDate - Date to use as a startint created_at date for the deliveries being taken into account.
   * @returns json object containing filtering values in the form:
   * {
   *    field1: [
   *       value1,
   *       value2,
   *       value3,
   *       ...
   *       ],
   *    field2: [
   *       value4,
   *       value5,
   *       value6,
   *       ...
   *       ],
   *    ...
   * }
   */
  async getLastMileDeliveriesFilterValues ({
    filters = {},
    applyUserFilters = false // to be only called from agave controller to merge with default user filters
  }) {
    const processedFilters = this.applyUserFiltersIfNeeded({
      filters,
      applyUserFilters
    })

    const result = await this.dataShipmentAdapter.getLastMileDeliveriesFilterValues(processedFilters)
    return get(result, 'rows[0].values', {})
  }

  /**
   * Proxified method (see ProxifiedEntityApi) to calculate shipment volume reading the data from Postgres.
   * @param {Object} params - Contains shipmentId.
   * @returns {Object} Object containing totalVolume and totalPrice.
   */
  async calculateVolume ({ shipmentIds } = {}) {
    if (!shipmentIds || !isArray(shipmentIds)) {
      throw new Error('shipmentIds required to get volume')
    }
    if (shipmentIds.length === 0) {
      return []
    }
    return this.dataShipmentAdapter.getVolume(shipmentIds)
  }

  /**
   * Proxified method. Computes shipment volumes from CouchDB-sourced batch counts.
   * Used as a fallback when postgres line tables (data_shipmentline / data_shipmentsnapshotline)
   * are empty, e.g. for newly created shipments not yet fully synced.
   *
   * @param {Object} params
   * @param {Object} params.countsByShipmentId - Map of shipmentId → counts object
   *   where counts is { batchId: { quantity: number } }
   * @returns {Promise<Array<{ shipment_id: string, total_volume: string }>>}
   */
  async calculateVolumeFromCounts ({ countsByShipmentId } = {}) {
    if (!countsByShipmentId || Object.keys(countsByShipmentId).length === 0) {
      return []
    }

    // Collect unique product IDs needed: manufacturer-specific and generic SKU
    const productIds = new Set()
    for (const counts of Object.values(countsByShipmentId)) {
      for (const batchId of Object.keys(counts || {})) {
        const manufacturerMatch = batchId.match(/manufacturer:([^:]+)/)
        if (manufacturerMatch) {
          productIds.add(`product:${manufacturerMatch[1]}`)
        }
        const parts = batchId.split(':')
        if (parts[0] === 'product' && parts[1]) {
          productIds.add(`product:${parts[1]}`)
        }
      }
    }

    const unitVolumes = await this.dataShipmentAdapter.getUnitVolumesForProductIds([...productIds])

    return Object.entries(countsByShipmentId).map(([shipmentId, counts]) => {
      let total = 0
      for (const [batchId, batchData] of Object.entries(counts || {})) {
        const quantity = (batchData && batchData.quantity) || 0
        if (!quantity) continue

        const manufacturerMatch = batchId.match(/manufacturer:([^:]+)/)
        const parts = batchId.split(':')
        const sku = parts[0] === 'product' && parts[1] ? parts[1] : null

        const unitVolume =
          (manufacturerMatch && unitVolumes[`product:${manufacturerMatch[1]}`]) ||
          (sku !== null && unitVolumes[`product:${sku}`]) ||
          0

        total += quantity * unitVolume
      }
      return {
        shipment_id: shipmentId,
        total_volume: String(Math.round(total * 1000000) / 1000000)
      }
    })
  }

  async calculateVolumeAndPrice (params) {
    return calculateShipmentVolumeAndPrice(params, this.mainApi)
  }

  applyUserFiltersIfNeeded ({ filters, applyUserFilters = false }) {
    return applyLMDUserFilters({ filters, applyUserFilters, user: this.user })
  }

  /**
   * Get CouchDB IDs and location IDs for shipment snapshots by route IDs
   * @param {string[]} routeIds - Array of route IDs to filter by
   * @param {object} options - Query options
   * @param {string[]} [options.programIds] - Filter by program IDs
   * @param {string} [options.cutoffDate] - ISO date string - only return snapshots updated after this date
   * @returns {Promise<{couchdbIds: string[], locationIds: string[]}>} Object with couchdbIds and unique locationIds
   */
  async getSnapshotCouchdbIdsByRoutes (routeIds, options = {}) {
    if (!this.shipmentSnapshotAdapter) {
      throw new Error('PostgreSQL connection not available')
    }
    return this.shipmentSnapshotAdapter.getCouchdbIdsByRoutes(routeIds, options)
  }

  /**
   * Get CouchDB IDs for change documents linked to the specified snapshots
   * @param {string[]} snapshotCouchdbIds - Array of snapshot CouchDB IDs
   * @returns {Promise<string[]>} Array of change document CouchDB IDs
   */
  async getSnapshotChangeDocIds (snapshotCouchdbIds) {
    if (!this.shipmentSnapshotAdapter) {
      throw new Error('PostgreSQL connection not available')
    }
    return this.shipmentSnapshotAdapter.getChangeDocIds(snapshotCouchdbIds)
  }

  /**
   * Get shipment snapshot data grouped by route ID for the cache producer Lambda.
   * Returns the same fields as getSnapshotCouchdbIdsByRoutes but grouped per route.
   * @param {object} options - Query options
   * @param {string} [options.cutoffDate] - ISO date string - only return snapshots updated after this date
   * @returns {Promise<Object<string, {couchdbIds: string[], locationIds: string[], funderIds: string[], serviceIds: string[], productIds: string[], changeDocIds: string[], lastUpdated: string, batchIds: string[]}>>} Map of routeId to route data
   */
  async getSnapshotsByRoutesGrouped (options = {}) {
    if (!this.shipmentSnapshotAdapter) {
      throw new Error('PostgreSQL connection not available')
    }
    return this.shipmentSnapshotAdapter.getCouchdbIdsByRoutesGrouped(options)
  }
}

async function updateOrderDocsRouteInCouchDB (orderDb, orderIds, routeId) {
  const MAX_RETRIES = 3
  const orderUpdateResults = await Promise.allSettled(orderIds.map(async orderId => {
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      let doc
      try {
        doc = await orderDb.get(orderId)
      } catch (err) {
        if (err.status === 404) return
        throw err
      }
      if (doc.routeId === routeId) return
      try {
        await orderDb.put({ ...doc, routeId })
        return
      } catch (err) {
        if (err.status === 409 && attempt < MAX_RETRIES - 1) continue
        throw err
      }
    }
  }))

  const orderFailures = []
  orderUpdateResults.forEach((res, idx) => {
    if (res.status === 'rejected') {
      console.error(`Error updating order doc ${orderIds[idx]} routeId in CouchDB:`, res.reason)
      orderFailures.push(orderIds[idx] || 'unknown')
    }
  })

  if (orderFailures.length) {
    throw new Error(`Failed to update routeId on ${orderFailures.length} order doc(s) in CouchDB`)
  }
}

class SurveyApi extends EntityApi {
  constructor (state, restAdapter) {
    const { user, shipmentsDb } = state
    // Heads up: this is the same adapter as shipments api
    const adapter = new ShipmentPouchAdapter(user, shipmentsDb)
    super(adapter)

    // TODO: remove this when all raw methods have been ported
    wrap(rawSurveyMethods, state, setupShipmentApi, null, this)
  }
}

module.exports.ShipmentApi = ShipmentApi
module.exports.SurveyApi = SurveyApi

module.exports.SHIPMENT_TYPE_LAST_MILE_DELIVERY = DataShipmentAdapter.SHIPMENT_TYPE_LAST_MILE_DELIVERY
module.exports.SHIPMENT_TYPE_FROM_WAREHOUSE = DataShipmentAdapter.SHIPMENT_TYPE_FROM_WAREHOUSE
module.exports.SHIPMENT_TYPE_ALL_TYPES = DataShipmentAdapter.SHIPMENT_TYPE_ALL_TYPES
